import React from 'react';
import { Helmet } from 'react-helmet-async';

const INDUSTRIES = {
  TRANSPORTATION:          'Transportation',
  TELECOM:                 'Telecom',
  HEAVY_EQUIPMENT:         'Heavy Equipment',
  INDUSTRIAL:              'Industrial Machinery',
  PUMPS_AND_COMPRESSORS:   'Pumps and Compressors',
  MEDICAL:                 'Medical',
  PNEUMATIC_AND_HYDRAULIC: 'Pneumatic and Hydraulic',
};

const products = [
  { name: 'Aluminium Bronze Flange',          image: '/images/products/product_13.webp', industry: INDUSTRIES.TRANSPORTATION,          alt: 'Aluminium bronze flange bushing precision-machined for automotive and transportation OEM applications - Mato Industries Delhi' },
  { name: 'Bronze Collar Bush',               image: '/images/products/product_15.webp', industry: INDUSTRIES.TRANSPORTATION,          alt: 'Bronze collar bush manufactured from phosphorous bronze for heavy-duty transportation and drivetrain assemblies - Mato Industries' },
  { name: 'High Tensile Brass Split Bush',    image: '/images/products/product_16.webp', industry: INDUSTRIES.INDUSTRIAL,              alt: 'High tensile brass split bush for industrial machinery applications requiring tight tolerances and high load capacity - Mato Industries' },
  { name: 'Bronze Piston',                    image: '/images/products/product_17.webp', industry: INDUSTRIES.TRANSPORTATION,          alt: 'Precision bronze piston component for transportation and automotive OEM assemblies, CNC machined to drawing - Mato Industries Delhi' },
  { name: 'Bronze Thrust Washer',             image: '/images/products/product_18.webp', industry: INDUSTRIES.HEAVY_EQUIPMENT,         alt: 'Bronze thrust washer for heavy equipment axle and gearbox assemblies, manufactured in leaded bronze and gun metal - Mato Industries' },
  { name: 'Brass Pneumatic Nozzle',           image: '/images/products/product_14.webp', industry: INDUSTRIES.PNEUMATIC_AND_HYDRAULIC, alt: 'Brass pneumatic nozzle precision-machined for pneumatic and hydraulic systems requiring corrosion resistance - Mato Industries Delhi' },
  { name: 'Threaded Bush',                    image: '/images/products/product_19.webp', industry: INDUSTRIES.TRANSPORTATION,          alt: 'Threaded bronze bush for transportation chassis and suspension components, machined to customer drawing - Mato Industries' },
  { name: 'Dielectric for Microwave Antenna', image: '/images/products/product_20.webp', industry: INDUSTRIES.TELECOM,                 alt: 'Precision dielectric component for microwave antenna telecom infrastructure, machined in aluminium - Mato Industries Delhi' },
  { name: 'Telecom Parts in Aluminium',       image: '/images/products/product_21.webp', industry: INDUSTRIES.TELECOM,                 alt: 'Custom aluminium telecom parts for microwave and antenna systems, CNC machined to tight tolerances - Mato Industries' },
  { name: 'Feed for Microwave Antenna',       image: '/images/products/product_22.webp', industry: INDUSTRIES.TELECOM,                 alt: 'Microwave antenna feed component precision-machined in aluminium for telecom infrastructure applications - Mato Industries Delhi' },
  { name: 'Bronze Shifter',                   image: '/images/products/product_23.webp', industry: INDUSTRIES.INDUSTRIAL,              alt: 'Bronze shifter component for industrial machinery gearboxes and transmission systems - Mato Industries Delhi' },
  { name: 'Brass Pneumatic Parts',            image: '/images/products/product_24.webp', industry: INDUSTRIES.PNEUMATIC_AND_HYDRAULIC, alt: 'Brass pneumatic parts for hydraulic and pneumatic control systems, precision-machined from free cutting brass - Mato Industries' },
  { name: 'Tractor Bush',                     image: '/images/products/product_25.webp', industry: INDUSTRIES.HEAVY_EQUIPMENT,         alt: 'Tractor bush in aluminium bronze for heavy equipment and agricultural machinery, high load and wear resistance - Mato Industries' },
  { name: 'Bronze Threaded Bush',             image: '/images/products/product_26.webp', industry: INDUSTRIES.TRANSPORTATION,          alt: 'Bronze threaded bush for automotive and transportation assemblies, manufactured to OEM specifications - Mato Industries Delhi' },
  { name: 'Tractor Bush (variant)',           image: '/images/products/product_27.webp', industry: INDUSTRIES.HEAVY_EQUIPMENT,         alt: 'Heavy-duty tractor bush variant in gun metal for agricultural and construction equipment drivetrain - Mato Industries Delhi' },
  { name: 'Hydraulic Pump Plate',             image: '/images/products/product_12.webp', industry: INDUSTRIES.PUMPS_AND_COMPRESSORS,   alt: 'Hydraulic pump plate precision-machined in bronze for high-pressure pump and compressor applications - Mato Industries Delhi' },
  { name: 'Bronze Piston (heavy)',            image: '/images/products/product_28.webp', industry: INDUSTRIES.TRANSPORTATION,          alt: 'Heavy-duty bronze piston for commercial vehicle and transportation OEM assemblies, CNC machined - Mato Industries Delhi' },
  { name: 'Bush for Compressor',              image: '/images/products/product_29.webp', industry: INDUSTRIES.PUMPS_AND_COMPRESSORS,   alt: 'Compressor bush in phosphorous bronze for industrial pumps and air compressor applications - Mato Industries Delhi' },
];

const alloys = [
  'Phosphorous Bronze', 'Gun Metal', 'Leaded Bronze',
  'Aluminium Bronze', 'High Tensile Brass', 'Free Cutting Brass',
];

const ProductsPage = () => (
  <>
    <Helmet>
      <title>Bronze Bushings & Copper Alloy Products – Mato Industries</title>
      <meta name="description" content="Browse 18+ precision-machined products: bronze bushings, thrust washers, tractor bushes, hydraulic pump plates & more. OEM-grade. Mato Industries, Delhi." />
      <link rel="canonical" href="https://www.matoindustries.com/products" />
      <meta property="og:url" content="https://www.matoindustries.com/products" />
      <meta property="og:title" content="Bronze Bushings & Copper Alloy Products – Mato Industries" />
      <meta property="og:description" content="Browse 18+ precision-machined products: bronze bushings, thrust washers, tractor bushes, hydraulic pump plates & more. OEM-grade. Mato Industries, Delhi." />
    </Helmet>
    <div className="min-h-screen bg-slate-50">

    {/* Page header */}
    <div className="bg-navy-900 text-white py-14 px-4">
      <div className="container mx-auto max-w-7xl">
        <span className="text-brand-400 text-sm font-semibold uppercase tracking-widest">Catalogue</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-2 mb-3">Our Products</h1>
        <p className="text-slate-400 text-lg max-w-2xl">
          Manufactured to customer drawings with close tolerances from castings, extruded bars, and drawn rods.
        </p>
      </div>
    </div>

    <main className="container mx-auto max-w-7xl px-4 sm:px-6 py-12">

      {/* Intro + alloys */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-3 text-slate-700 text-sm leading-relaxed">
            <p>
              We specialize in <strong>Bronze Bushings / Bushes</strong>, <strong>Thrust Washers</strong>,{' '}
              <strong>Bronze Bearings</strong>, and more. Our deep understanding of alloys means we can also suggest
              the best material for your application and performance requirements.
            </p>
            <p>
              Components are produced from <strong>Sand Castings</strong>, <strong>Gravity Die Casting</strong>,{' '}
              <strong>Continuous Castings</strong>, and <strong>Extruded Bars</strong>, all machined to close
              tolerances per customer drawings.
            </p>
            <p>
              We serve as <strong>OEM suppliers</strong> to Tractors, Earth Movers, Industrial Machinery,
              Refrigeration, Railways, Pumps, and Telecom sectors.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Alloys We Work With</p>
            <div className="flex flex-wrap gap-2">
              {alloys.map((a) => (
                <span
                  key={a}
                  className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-navy-700"
                >
                  {a}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              We also work with other alloys on request. Contact us with your material requirements.
            </p>
          </div>
        </div>
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {products.map((product, index) => (
          <div
            key={index}
            className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-default"
          >
            <div className="aspect-square overflow-hidden bg-slate-50">
              <img
                src={product.image}
                alt={product.alt}
                loading="lazy"
                width="300"
                height="300"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="p-2.5">
              <p className="text-xs font-semibold text-navy-800 leading-snug group-hover:text-navy-900 transition-colors">
                {product.name}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Request a quote CTA */}
      <div className="mt-12 bg-navy-900 rounded-2xl p-8 text-center text-white">
        <h2 className="text-2xl font-extrabold mb-2">Don't see what you need?</h2>
        <p className="text-slate-400 mb-6 text-sm max-w-lg mx-auto">
          We manufacture custom components from your drawings. Send us your specifications and we'll respond within 24 hours.
        </p>
        <a
          href="/contact"
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg transition-colors duration-200 cursor-pointer"
        >
          Request a Quote
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </a>
      </div>

    </main>
    </div>
  </>
);

export default ProductsPage;
